// AP-049 · Company Selector — visible only to Master Super Admin.
// Instant tenant switch (no logout). Adds a subtle "DEMO" badge for demo tenants.
import React, { useState } from "react";
import { Building2, Check, ChevronDown, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import { useTenant } from "../context/TenantContext";

export default function CompanySelector() {
  const { tenants, currentTenant, currentTenantMeta, isMaster, switchTenant, seedDemo } = useTenant();
  const [seeding, setSeeding] = useState(false);
  if (!isMaster) return null;

  const active = currentTenantMeta;
  const isDemo = !!active?.demo;

  const handleSwitch = (id) => {
    if (id === currentTenant) return;
    switchTenant(id);
    const meta = tenants.find((t) => t.id === id);
    toast.success(`Empresa ativa: ${meta?.name || id}`, {
      description: meta?.demo ? "Modo Demo isolado — nenhum dado JMS é afetado." : "Produção JMS — dados reais.",
    });
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const data = await seedDemo();
      const summary = Object.entries(data.seeded || {})
        .map(([tid, s]) => s.skipped ? `${tid}: já populado` : `${tid}: ${s.customers}c/${s.processes}p`)
        .join(" · ");
      toast.success("Seed demo concluído", { description: summary });
      window.dispatchEvent(new CustomEvent("jms:tenant-changed", { detail: { tenant: currentTenant } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao popular tenants demo.");
    } finally { setSeeding(false); }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="company-selector-trigger"
          className={
            "inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs font-semibold transition " +
            (isDemo
              ? "border-primary/30 bg-primary/5 text-primary hover:bg-primary/10"
              : "border-slate-200 bg-white hover:bg-slate-50 text-slate-800")
          }
          title="Trocar empresa (Master)"
        >
          <span
            className="h-6 w-6 rounded-md grid place-items-center text-white text-[10px] font-bold shrink-0"
            style={{ background: active?.color || "#0057B8" }}
          >
            {(active?.short || "JMS").slice(0, 3)}
          </span>
          <span className="hidden sm:inline max-w-[180px] truncate">{active?.name || "JMS MULTI SERVICE LTDA"}</span>
          {isDemo && <Badge className="hidden md:inline-flex bg-primary/10 text-primary border border-primary/30 text-[9px] px-1.5 py-0">DEMO</Badge>}
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72" data-testid="company-selector-menu">
        <DropdownMenuLabel className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
          <Building2 className="h-3 w-3" /> Empresas disponíveis (Master)
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tenants.map((t) => (
          <DropdownMenuItem
            key={t.id}
            onSelect={() => handleSwitch(t.id)}
            data-testid={`company-selector-item-${t.id}`}
            className="flex items-center gap-2 py-2 cursor-pointer"
          >
            <span
              className="h-6 w-6 rounded-md grid place-items-center text-white text-[10px] font-bold shrink-0"
              style={{ background: t.color || "#0057B8" }}
            >
              {(t.short || "JMS").slice(0, 3)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                {t.name}
                {t.demo && <Badge className="bg-primary/10 text-primary border border-primary/30 text-[9px] px-1.5 py-0">DEMO</Badge>}
              </p>
              <p className="text-[10px] text-muted-foreground truncate">{t.id}</p>
            </div>
            {t.id === currentTenant && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); handleSeed(); }}
          data-testid="company-selector-seed"
          className="flex items-center gap-2 py-2 text-xs text-primary cursor-pointer"
        >
          {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Popular dados demo (idempotente)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
